import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, TextField, Select, MenuItem,
  FormControl, InputLabel, CircularProgress, Alert, IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../api/axiosConfig';

const Subjects = () => {
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);

  const [formData, setFormData] = useState({
    courseId: '',
    subjectName: '',
    maxMarks: '',
    minMarks: ''
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (selectedCourse) fetchSubjects(selectedCourse);
  }, [selectedCourse]);

  // 📘 Fetch Courses
  const fetchCourses = async () => {
    try {
      const res = await axiosInstance.get('/courses');
      const data = res.data;
      setCourses(data);
      if (data.length > 0) setSelectedCourse(data[0].courseId);
    } catch (err) {
      setError('Failed to load courses');
    }
  };

  // 📘 Fetch Subjects by Course
  const fetchSubjects = async (courseId) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/subjects/by-course/${courseId}`);
      setSubjects(res.data);
    } catch {
      setError('Failed to load subjects');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (subject = null) => {
    if (subject) {
      setEditingSubject(subject);
      setFormData({
        courseId: subject.courseId,
        subjectName: subject.subjectName,
        maxMarks: subject.maxMarks,
        minMarks: subject.minMarks
      });
    } else {
      setEditingSubject(null);
      setFormData({
        courseId: selectedCourse,
        subjectName: '',
        maxMarks: '',
        minMarks: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSubject(null);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // ✅ SAVE SUBJECT
  const handleSave = async () => {
    try {
      const payload = {
        courseId: parseInt(formData.courseId),
        subjectName: formData.subjectName,
        maxMarks: parseInt(formData.maxMarks),
        minMarks: parseInt(formData.minMarks)
      };

      if (editingSubject) {
        await axiosInstance.put(`/subjects/${editingSubject.subjectId}`, payload);
      } else {
        await axiosInstance.post('/subjects', payload);
      }

      handleCloseDialog();
      fetchSubjects(selectedCourse);
    } catch (err) {
      console.error(err);
      alert('Error saving subject');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this subject?')) {
      await axiosInstance.delete(`/subjects/${id}`);
      fetchSubjects(selectedCourse);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h4" fontWeight="bold">Subject Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add Subject
        </Button>
      </Box>

      <Card sx={{ mb: 3, p: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Course</InputLabel>
          <Select value={selectedCourse} label="Course" onChange={(e) => setSelectedCourse(e.target.value)}>
            {courses.map(course => (
              <MenuItem key={course.courseId} value={course.courseId}>
                {course.courseName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Card>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#1976d2' }}>
              <TableRow>
                <TableCell sx={{ color: 'white' }}>Subject</TableCell>
                <TableCell sx={{ color: 'white' }}>Max Marks</TableCell>
                <TableCell sx={{ color: 'white' }}>Min Marks</TableCell>
                <TableCell sx={{ color: 'white' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subjects.map(sub => (
                <TableRow key={sub.subjectId}>
                  <TableCell>{sub.subjectName}</TableCell>
                  <TableCell>{sub.maxMarks}</TableCell>
                  <TableCell>{sub.minMarks}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleOpenDialog(sub)} color="primary"><EditIcon /></IconButton>
                    <IconButton onClick={() => handleDelete(sub.subjectId)} color="error"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSubject ? 'Edit Subject' : 'Add Subject'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Course</InputLabel>
                <Select name="courseId" value={formData.courseId} onChange={handleChange}>
                  {courses.map(course => (
                    <MenuItem key={course.courseId} value={course.courseId}>
                      {course.courseName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Subject Name" name="subjectName" value={formData.subjectName} onChange={handleChange} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Max Marks" name="maxMarks" value={formData.maxMarks} onChange={handleChange} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Min Marks" name="minMarks" value={formData.minMarks} onChange={handleChange} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subjects;
